import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { IS_THAILAND } from '../../utils/datePicker';

export const useDateValidation = (latestScheduledUtc, setMaxScheduleError = () => {}) => {
  let DATE_FORMAT = useSelector(
    (state) => state?.uiState?.remoteConfig?.date_format || 'MM/DD/YYYY'
  );
  DATE_FORMAT = IS_THAILAND ? 'DD/MM/YYYY' : DATE_FORMAT;

  const getDateParts = (date) => {
    let dateParts;
  
    switch (DATE_FORMAT) {
      case 'DD/MM/YYYY':
      case 'MM/DD/YYYY':
        dateParts = date.split('/').map(Number);
        break;
  
      case 'YYYY-MM-DD':
      case 'YYYY-DD-MM':
        dateParts = date.split('-').map(Number);
        break;
      
      case 'YYYY年M月D日': {
        const match = date.match(/^(\d{4})年(\d{1,2})月(\d{1,2})日$/);
  
        if (!match) return {};
  
        const [, year, month, day] = match.map(Number);
        return { year, month, day };
      }
    
  
      default:
        return {};
    }
  
    switch (DATE_FORMAT) {
      case 'DD/MM/YYYY': {
        const [day, month, year] = dateParts;
        return { month, day, year };
      }
  
      case 'MM/DD/YYYY': {
        const [month, day, year] = dateParts;
        return { month, day, year };
      }
  
      case 'YYYY-MM-DD': {
        const [year, month, day] = dateParts;
        return { month, day, year };
      }
  
      case 'YYYY-DD-MM': {
        const [year, day, month] = dateParts;
        return { month, day, year };
      }
  
      default:
        return {};
    }
  };
  

  const validateDate = (date, dateType = '') => {
    const { month, day, year } = getDateParts(date);

    if (!month || !day || !year) {
      return `Date must be in ${DATE_FORMAT} format`;
    }

    if (isNaN(month) || isNaN(day) || isNaN(year) || year < 1000 || year > 9999) {
      return `Date must be in ${DATE_FORMAT} format`;
    }

    if (month < 1 || month > 12) {
      return `Month must be between 1 and 12`;
    }

    if (day < 1 || day > 31) {
      return `Day must be between 1 and 31`;
    }

    if (!dayjs(date, DATE_FORMAT, true).isValid()) {
      return `Invalid ${dateType} Date`;
    }

    const inputDate = dayjs(date, DATE_FORMAT);
    const currentDate = IS_THAILAND ? dayjs().add(543, 'year') : dayjs();
    const maxFutureDate = currentDate.add(30, 'day');

    if (inputDate.isBefore(currentDate, 'day')) {
      return `${dateType || 'Selected'} date cannot be in the past.`;
    }

    if (inputDate.isAfter(maxFutureDate, 'day')) {
      return `${dateType || 'Selected'} date cannot be more than 30 days in the future.`;
    }

    // Clear the max schedule error here if date validation is OK
    setMaxScheduleError(null);
    return true;
  };

  const validateTime = (time) => {
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;

    if (!timeRegex.test(time)) {
      const [hours, minutes] = time.split(':');

      if (parseInt(hours) < 0 || parseInt(hours) > 23) {
        return `Hours must be between 00 and 23.`;
      }

      if (parseInt(minutes) < 0 || parseInt(minutes) > 59) {
        return `Minutes must be between 00 and 59.`;
      }

      return `Time must be in HH:MM format (24-hour).`;
    }

    return true;
  };

  const validateTimeWithCondition = (time, validateNext) => {
    const result = validateTime(time);
    if (result !== true) return result;

    if (validateNext) {
      return validateNext();
    }

    return true;
  };

  const validateStartTime = (startTime, startDate) => {
    if (!startTime || !startDate) {
      setMaxScheduleError(null);
      return true;
    }

    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    if (!timeRegex.test(startTime) || startTime.length !== 5) {
      setMaxScheduleError(null);
      return 'Invalid Start Time';
    }

    const inputDateTime = dayjs(`${startDate} ${startTime}`, `${DATE_FORMAT} HH:mm`, true);

    if (!inputDateTime.isValid()) {
      setMaxScheduleError(null);
      return 'Invalid Start Date or Time';
    }

    const now = dayjs();
    const maxFutureDate = IS_THAILAND ? now.add(30, 'day').add(543, 'year') : now.add(30, 'day');

    if (inputDateTime.isBefore(now)) {
      setMaxScheduleError(null);
      return 'Start time cannot be in the past.';
    }

    if (inputDateTime.isAfter(maxFutureDate)) {
      setMaxScheduleError(null);
      return 'Start time cannot be more than 30 days in the future.';
    }

    if (
      latestScheduledUtc &&
      dayjs(latestScheduledUtc).isValid()
    ) {
      const latestScheduled = dayjs(latestScheduledUtc);
  
      // Only apply max schedule error if latestScheduled is in the future
      if (inputDateTime.isAfter(latestScheduled) && latestScheduled.isAfter(now)) {
        const errorMsg = `${latestScheduled.format(`${DATE_FORMAT} HH:mm`)}`;
        setMaxScheduleError(errorMsg);
        return true;
      }
    }
  
    setMaxScheduleError(null);
    return true;
  };

  return {
    DATE_FORMAT,
    validateDate,
    validateTime,
    validateTimeWithCondition,
    validateStartTime,
  };
};
